import api from './api';

/** Campos contables (número de cuenta imputable). Alineado a `EstrategiaDto` en la API. */
export interface Estrategia {
    nint: number;
    nombre: string;
    tipo: string;
    activo: string;
    descripcion?: string;
    disponibilidades?: number | null;
    bancosCtaCte?: number | null;
    clientes?: number | null;
    otrosCreditos?: number | null;
    bienesDeUso?: number | null;
    proveedores?: number | null;
    ivaDebitoFiscal?: number | null;
    cargasPrevisiona?: number | null;
    cargaSociales?: number | null;
    egresos?: number | null;
    ctosDirectos?: number | null;
    ivaCreditoFiscal?: number | null;
    costosIndirectos?: number | null;
    ingresos?: number | null;
    vtasDirectas?: number | null;
    ventasCanje?: number | null;
    otrosIngresos?: number | null;
    obligComAPagar?: number | null;
    obligBancarias?: number | null;
    cajaEfectivo?: number | null;
    cajaValTerceros?: number | null;
    valPendDepositos?: number | null;
    cajaMonedaextran?: number | null;
    cajaBonos?: number | null;
    ivaDebFiscal?: number | null;
    ivaDebFiscNoInsc?: number | null;
    ivaDebFiscBdeUso?: number | null;
    ivaDebFisBsVsNoI?: number | null;
    ivaCredFiscal?: number | null;
    ivaCreFiscNoInsc?: number | null;
    ivaCreFiscBdeUso?: number | null;
    ivaCreFisBsVsNoI?: number | null;
    ivarg3337?: number | null;
    sueldJornalaPag?: number | null;
    manoObraDirecta?: number | null;
    intComerciales?: number | null;
    amortizaciones?: number | null;
    rdoVtaBsUso?: number | null;
    revaluoBsdeUso?: number | null;
    rdoExpInflacion?: number | null;
    impuestos?: number | null;
    tarjetasCredito?: number | null;
    retencionIVA?: number | null;
    ivaRetenidoX3?: number | null;
    consumidorFinal?: number | null;
    gastosTarjetas?: number | null;
    rg3311?: number | null;
    documentosCobrar?: number | null;
    fletes?: number | null;
    descuentosVentas?: number | null;
    descuentosCompras?: number | null;
    nd?: number | null;
    impInterno1?: number | null;
    impInterno2?: number | null;
    tasaHidrica?: number | null;
    salINIClie?: number | null;
    salINIProv?: number | null;
    otrosImpuestos?: number | null;
    ctaGenCuotas?: number | null;
    ndNeto?: number | null;
    ndExento?: number | null;
}

export type EstrategiaImputableFieldKey = keyof Pick<
    Estrategia,
    | 'disponibilidades'
    | 'bancosCtaCte'
    | 'clientes'
    | 'otrosCreditos'
    | 'bienesDeUso'
    | 'proveedores'
    | 'ivaDebitoFiscal'
    | 'cargasPrevisiona'
    | 'cargaSociales'
    | 'egresos'
    | 'ctosDirectos'
    | 'ivaCreditoFiscal'
    | 'costosIndirectos'
    | 'ingresos'
    | 'vtasDirectas'
    | 'ventasCanje'
    | 'otrosIngresos'
    | 'obligComAPagar'
    | 'obligBancarias'
    | 'cajaEfectivo'
    | 'cajaValTerceros'
    | 'valPendDepositos'
    | 'cajaMonedaextran'
    | 'cajaBonos'
    | 'ivaDebFiscal'
    | 'ivaDebFiscNoInsc'
    | 'ivaDebFiscBdeUso'
    | 'ivaDebFisBsVsNoI'
    | 'ivaCredFiscal'
    | 'ivaCreFiscNoInsc'
    | 'ivaCreFiscBdeUso'
    | 'ivaCreFisBsVsNoI'
    | 'ivarg3337'
    | 'sueldJornalaPag'
    | 'manoObraDirecta'
    | 'intComerciales'
    | 'amortizaciones'
    | 'rdoVtaBsUso'
    | 'revaluoBsdeUso'
    | 'rdoExpInflacion'
    | 'impuestos'
    | 'tarjetasCredito'
    | 'retencionIVA'
    | 'ivaRetenidoX3'
    | 'consumidorFinal'
    | 'gastosTarjetas'
    | 'rg3311'
    | 'documentosCobrar'
    | 'fletes'
    | 'descuentosVentas'
    | 'descuentosCompras'
    | 'nd'
    | 'impInterno1'
    | 'impInterno2'
    | 'tasaHidrica'
    | 'salINIClie'
    | 'salINIProv'
    | 'otrosImpuestos'
    | 'ctaGenCuotas'
    | 'ndNeto'
    | 'ndExento'
>;

/** Metadatos para armar el formulario (una fila por campo de la tabla Estrategia). */
export const ESTRATEGIA_IMPUTABLE_FIELDS: { key: EstrategiaImputableFieldKey; label: string }[] = [
    { key: 'disponibilidades', label: 'Disponibilidades' },
    { key: 'bancosCtaCte', label: 'Bancos c/c' },
    { key: 'clientes', label: 'Clientes' },
    { key: 'otrosCreditos', label: 'Otros créditos' },
    { key: 'bienesDeUso', label: 'Bienes de uso' },
    { key: 'proveedores', label: 'Proveedores' },
    { key: 'ivaDebitoFiscal', label: 'IVA débito fiscal' },
    { key: 'cargasPrevisiona', label: 'Cargas previsionales' },
    { key: 'cargaSociales', label: 'Cargas sociales' },
    { key: 'egresos', label: 'Egresos' },
    { key: 'ctosDirectos', label: 'Costos directos' },
    { key: 'ivaCreditoFiscal', label: 'IVA crédito fiscal' },
    { key: 'costosIndirectos', label: 'Costos indirectos' },
    { key: 'ingresos', label: 'Ingresos' },
    { key: 'vtasDirectas', label: 'Ventas directas' },
    { key: 'ventasCanje', label: 'Ventas canje' },
    { key: 'otrosIngresos', label: 'Otros ingresos' },
    { key: 'obligComAPagar', label: 'Oblig. comerciales a pagar' },
    { key: 'obligBancarias', label: 'Obligaciones bancarias' },
    { key: 'cajaEfectivo', label: 'Caja efectivo' },
    { key: 'cajaValTerceros', label: 'Caja valores de terceros' },
    { key: 'valPendDepositos', label: 'Valores pendientes de depósito' },
    { key: 'cajaMonedaextran', label: 'Caja moneda extranjera' },
    { key: 'cajaBonos', label: 'Caja bonos' },
    { key: 'ivaDebFiscal', label: 'IVA débito fiscal (alt.)' },
    { key: 'ivaDebFiscNoInsc', label: 'IVA déb. fiscal no inscripto' },
    { key: 'ivaDebFiscBdeUso', label: 'IVA déb. fiscal bienes de uso' },
    { key: 'ivaDebFisBsVsNoI', label: 'IVA déb. fiscal bienes vs no inscripto' },
    { key: 'ivaCredFiscal', label: 'IVA crédito fiscal (alt.)' },
    { key: 'ivaCreFiscNoInsc', label: 'IVA créd. fiscal no inscripto' },
    { key: 'ivaCreFiscBdeUso', label: 'IVA créd. fiscal bienes de uso' },
    { key: 'ivaCreFisBsVsNoI', label: 'IVA créd. fiscal bienes vs no inscripto' },
    { key: 'ivarg3337', label: 'IVA RG 3337' },
    { key: 'sueldJornalaPag', label: 'Sueldos y jornales a pagar' },
    { key: 'manoObraDirecta', label: 'Mano de obra directa' },
    { key: 'intComerciales', label: 'Intereses comerciales' },
    { key: 'amortizaciones', label: 'Amortizaciones' },
    { key: 'rdoVtaBsUso', label: 'Resultado venta bienes de uso' },
    { key: 'revaluoBsdeUso', label: 'Revalúo bienes de uso' },
    { key: 'rdoExpInflacion', label: 'Resultado exposición inflación' },
    { key: 'impuestos', label: 'Impuestos' },
    { key: 'tarjetasCredito', label: 'Tarjetas de crédito' },
    { key: 'retencionIVA', label: 'Retención IVA' },
    { key: 'ivaRetenidoX3', label: 'IVA retenido 3%' },
    { key: 'consumidorFinal', label: 'Consumidor final' },
    { key: 'gastosTarjetas', label: 'Gastos tarjetas' },
    { key: 'rg3311', label: 'RG 3311' },
    { key: 'documentosCobrar', label: 'Documentos a cobrar' },
    { key: 'fletes', label: 'Fletes' },
    { key: 'descuentosVentas', label: 'Descuentos ventas' },
    { key: 'descuentosCompras', label: 'Descuentos compras' },
    { key: 'nd', label: 'ND' },
    { key: 'impInterno1', label: 'Impuesto interno 1' },
    { key: 'impInterno2', label: 'Impuesto interno 2' },
    { key: 'tasaHidrica', label: 'Tasa hídrica' },
    { key: 'salINIClie', label: 'Saldo inicial clientes' },
    { key: 'salINIProv', label: 'Saldo inicial proveedores' },
    { key: 'otrosImpuestos', label: 'Otros impuestos' },
    { key: 'ctaGenCuotas', label: 'Cuenta general cuotas' },
    { key: 'ndNeto', label: 'ND neto' },
    { key: 'ndExento', label: 'ND exento' }
];

/** Sub-solapas por módulo funcional (Compras, Ventas, Tesorería, resto contable). */
export type EstrategiaImputableModulo = 'compras' | 'ventas' | 'tesoreria' | 'otros';

export const ESTRATEGIA_IMPUTABLE_SUBTABS: {
    modulo: EstrategiaImputableModulo;
    label: string;
    keys: readonly EstrategiaImputableFieldKey[];
}[] = [
    {
        modulo: 'compras',
        label: 'Compras',
        keys: [
            'proveedores',
            'obligComAPagar',
            'salINIProv',
            'ivaCreditoFiscal',
            'ivaCredFiscal',
            'ivaCreFiscNoInsc',
            'ivaCreFiscBdeUso',
            'ivaCreFisBsVsNoI',
            'bienesDeUso',
            'ctosDirectos',
            'costosIndirectos',
            'egresos',
            'descuentosCompras',
            'fletes',
            'nd',
            'ndNeto',
            'ndExento',
            'impInterno1',
            'impInterno2',
            'otrosImpuestos',
            'tasaHidrica'
        ]
    },
    {
        modulo: 'ventas',
        label: 'Ventas',
        keys: [
            'clientes',
            'documentosCobrar',
            'salINIClie',
            'ingresos',
            'vtasDirectas',
            'ventasCanje',
            'otrosIngresos',
            'otrosCreditos',
            'descuentosVentas',
            'ivaDebitoFiscal',
            'ivaDebFiscal',
            'ivaDebFiscNoInsc',
            'ivaDebFiscBdeUso',
            'ivaDebFisBsVsNoI',
            'consumidorFinal'
        ]
    },
    {
        modulo: 'tesoreria',
        label: 'Tesorería',
        keys: [
            'disponibilidades',
            'bancosCtaCte',
            'obligBancarias',
            'cajaEfectivo',
            'cajaValTerceros',
            'valPendDepositos',
            'cajaMonedaextran',
            'cajaBonos',
            'tarjetasCredito',
            'gastosTarjetas',
            'retencionIVA',
            'ivaRetenidoX3',
            'ivarg3337'
        ]
    },
    {
        modulo: 'otros',
        label: 'Otros',
        keys: [
            'cargasPrevisiona',
            'cargaSociales',
            'sueldJornalaPag',
            'manoObraDirecta',
            'intComerciales',
            'amortizaciones',
            'rdoVtaBsUso',
            'revaluoBsdeUso',
            'rdoExpInflacion',
            'impuestos',
            'rg3311',
            'ctaGenCuotas'
        ]
    }
];

const _estrategiaSubtabKeysFlat = ESTRATEGIA_IMPUTABLE_SUBTABS.flatMap((s) => [...s.keys]);
const _estrategiaSubtabKeysSet = new Set(_estrategiaSubtabKeysFlat);
const _estrategiaMasterKeys = new Set(ESTRATEGIA_IMPUTABLE_FIELDS.map((f) => f.key));
if (
    _estrategiaSubtabKeysFlat.length !== ESTRATEGIA_IMPUTABLE_FIELDS.length ||
    _estrategiaSubtabKeysSet.size !== ESTRATEGIA_IMPUTABLE_FIELDS.length ||
    _estrategiaSubtabKeysSet.size !== _estrategiaMasterKeys.size ||
    Array.from(_estrategiaMasterKeys).some((k) => !_estrategiaSubtabKeysSet.has(k))
) {
    throw new Error(
        'ESTRATEGIA_IMPUTABLE_SUBTABS debe listar sin duplicados los mismos campos que ESTRATEGIA_IMPUTABLE_FIELDS'
    );
}

function readInt(
    raw: Record<string, unknown>,
    candidates: string[]
): number | null | undefined {
    for (const k of candidates) {
        const v = raw[k];
        if (v === undefined || v === null || v === '') continue;
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return undefined;
}

/** Unifica respuestas JSON camelCase / PascalCase y variantes de siglas (`iVADebitoFiscal`). */
export function normalizeEstrategiaFromApi(raw: unknown): Partial<Estrategia> {
    if (raw == null || typeof raw !== 'object') return {};
    const o = raw as Record<string, unknown>;

    const n =
        readInt(o, ['nint', 'Nint']) ??
        0;

    const out: Partial<Estrategia> = {
        nint: n,
        nombre: String(o.nombre ?? o.Nombre ?? ''),
        tipo: String(o.tipo ?? o.Tipo ?? 'C'),
        activo: String(o.activo ?? o.Activo ?? 'S'),
        descripcion: o.descripcion != null || o.Descripcion != null ? String(o.descripcion ?? o.Descripcion ?? '') : undefined,
        disponibilidades: readInt(o, ['disponibilidades', 'Disponibilidades']) ?? null,
        bancosCtaCte: readInt(o, ['bancosCtaCte', 'BancosCtaCte']) ?? null,
        clientes: readInt(o, ['clientes', 'Clientes']) ?? null,
        otrosCreditos: readInt(o, ['otrosCreditos', 'OtrosCreditos']) ?? null,
        bienesDeUso: readInt(o, ['bienesDeUso', 'BienesDeUso']) ?? null,
        proveedores: readInt(o, ['proveedores', 'Proveedores']) ?? null,
        ivaDebitoFiscal:
            readInt(o, ['ivaDebitoFiscal', 'iVADebitoFiscal', 'IVADebitoFiscal']) ?? null,
        cargasPrevisiona: readInt(o, ['cargasPrevisiona', 'CargasPrevisiona']) ?? null,
        cargaSociales: readInt(o, ['cargaSociales', 'CargaSociales']) ?? null,
        egresos: readInt(o, ['egresos', 'Egresos']) ?? null,
        ctosDirectos: readInt(o, ['ctosDirectos', 'CtosDirectos']) ?? null,
        ivaCreditoFiscal:
            readInt(o, ['ivaCreditoFiscal', 'iVACreditoFiscal', 'IVACreditoFiscal']) ?? null,
        costosIndirectos: readInt(o, ['costosIndirectos', 'CostosIndirectos']) ?? null,
        ingresos: readInt(o, ['ingresos', 'Ingresos']) ?? null,
        vtasDirectas: readInt(o, ['vtasDirectas', 'VtasDirectas']) ?? null,
        ventasCanje: readInt(o, ['ventasCanje', 'VentasCanje']) ?? null,
        otrosIngresos: readInt(o, ['otrosIngresos', 'OtrosIngresos']) ?? null,
        obligComAPagar: readInt(o, ['obligComAPagar', 'ObligComAPagar']) ?? null,
        obligBancarias: readInt(o, ['obligBancarias', 'ObligBancarias']) ?? null,
        cajaEfectivo: readInt(o, ['cajaEfectivo', 'CajaEfectivo']) ?? null,
        cajaValTerceros: readInt(o, ['cajaValTerceros', 'CajaValTerceros']) ?? null,
        valPendDepositos: readInt(o, ['valPendDepositos', 'ValPendDepositos']) ?? null,
        cajaMonedaextran: readInt(o, ['cajaMonedaextran', 'CajaMonedaextran']) ?? null,
        cajaBonos: readInt(o, ['cajaBonos', 'CajaBonos']) ?? null,
        ivaDebFiscal: readInt(o, ['ivaDebFiscal', 'iVADebFiscal', 'IVADebFiscal']) ?? null,
        ivaDebFiscNoInsc: readInt(o, ['ivaDebFiscNoInsc', 'iVADebFiscNoInsc', 'IVADebFiscNoInsc']) ?? null,
        ivaDebFiscBdeUso: readInt(o, ['ivaDebFiscBdeUso', 'iVADebFiscBdeUso', 'IVADebFiscBdeUso']) ?? null,
        ivaDebFisBsVsNoI: readInt(o, ['ivaDebFisBsVsNoI', 'iVADebFisBsVsNoI', 'IVADebFisBsVsNoI']) ?? null,
        ivaCredFiscal: readInt(o, ['ivaCredFiscal', 'iVACredFiscal', 'IVACredFiscal']) ?? null,
        ivaCreFiscNoInsc: readInt(o, ['ivaCreFiscNoInsc', 'iVACreFiscNoInsc', 'IVACreFiscNoInsc']) ?? null,
        ivaCreFiscBdeUso: readInt(o, ['ivaCreFiscBdeUso', 'iVACreFiscBdeUso', 'IVACreFiscBdeUso']) ?? null,
        ivaCreFisBsVsNoI: readInt(o, ['ivaCreFisBsVsNoI', 'iVACreFisBsVsNoI', 'IVACreFisBsVsNoI']) ?? null,
        ivarg3337: readInt(o, ['ivarg3337', 'iVARG3337', 'IVARG3337']) ?? null,
        sueldJornalaPag: readInt(o, ['sueldJornalaPag', 'SueldJornalaPag']) ?? null,
        manoObraDirecta: readInt(o, ['manoObraDirecta', 'ManoObraDirecta']) ?? null,
        intComerciales: readInt(o, ['intComerciales', 'IntComerciales']) ?? null,
        amortizaciones: readInt(o, ['amortizaciones', 'Amortizaciones']) ?? null,
        rdoVtaBsUso: readInt(o, ['rdoVtaBsUso', 'RdoVtaBsUso']) ?? null,
        revaluoBsdeUso: readInt(o, ['revaluoBsdeUso', 'RevaluoBsdeUso']) ?? null,
        rdoExpInflacion: readInt(o, ['rdoExpInflacion', 'RdoExpInflacion']) ?? null,
        impuestos: readInt(o, ['impuestos', 'Impuestos']) ?? null,
        tarjetasCredito: readInt(o, ['tarjetasCredito', 'TarjetasCredito']) ?? null,
        retencionIVA: readInt(o, ['retencionIVA', 'RetencionIVA']) ?? null,
        ivaRetenidoX3: readInt(o, ['ivaRetenidoX3', 'IVARetenidoX3']) ?? null,
        consumidorFinal: readInt(o, ['consumidorFinal', 'ConsumidorFinal']) ?? null,
        gastosTarjetas: readInt(o, ['gastosTarjetas', 'GastosTarjetas']) ?? null,
        rg3311: readInt(o, ['rg3311', 'rG3311', 'RG3311']) ?? null,
        documentosCobrar: readInt(o, ['documentosCobrar', 'DocumentosCobrar']) ?? null,
        fletes: readInt(o, ['fletes', 'Fletes']) ?? null,
        descuentosVentas: readInt(o, ['descuentosVentas', 'DescuentosVentas']) ?? null,
        descuentosCompras: readInt(o, ['descuentosCompras', 'DescuentosCompras']) ?? null,
        nd: readInt(o, ['nd', 'ND']) ?? null,
        impInterno1: readInt(o, ['impInterno1', 'ImpInterno1']) ?? null,
        impInterno2: readInt(o, ['impInterno2', 'ImpInterno2']) ?? null,
        tasaHidrica: readInt(o, ['tasaHidrica', 'TasaHidrica']) ?? null,
        salINIClie: readInt(o, ['salINIClie', 'SalINIClie']) ?? null,
        salINIProv: readInt(o, ['salINIProv', 'SalINIProv']) ?? null,
        otrosImpuestos: readInt(o, ['otrosImpuestos', 'OtrosImpuestos']) ?? null,
        ctaGenCuotas: readInt(o, ['ctaGenCuotas', 'CtaGenCuotas']) ?? null,
        ndNeto: readInt(o, ['ndNeto', 'iNDNeto', 'NDNeto']) ?? null,
        ndExento: readInt(o, ['ndExento', 'iNDExento', 'NDExento']) ?? null
    };

    return out;
}

export interface EstrategiaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
}

export interface EstrategiaPagedResult {
    items: Estrategia[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const estrategiaService = {
    getEstrategias: async (params: EstrategiaSearchParams): Promise<EstrategiaPagedResult> => {
        const response = await api.get<EstrategiaPagedResult>('/api/Estrategia', { params });
        return response.data;
    },

    getEstrategia: async (nint: number): Promise<Estrategia> => {
        const response = await api.get<Estrategia>(`/api/Estrategia/${nint}`);
        return response.data;
    },

    createEstrategia: async (estrategia: Estrategia): Promise<Estrategia> => {
        const response = await api.post<Estrategia>('/api/Estrategia', estrategia);
        return response.data;
    },

    updateEstrategia: async (nint: number, estrategia: Estrategia): Promise<void> => {
        await api.put(`/api/Estrategia/${nint}`, estrategia);
    },

    deleteEstrategia: async (nint: number): Promise<void> => {
        await api.delete(`/api/Estrategia/${nint}`);
    }
};

export default estrategiaService;
